import { Chart, registerables } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

interface EVFleetAdoptionProps {
  location: string;
  geofips?: number | null;
  activeTab?: 'perCapita' | 'projection';
  embedOnly?: boolean;
  onTabChange?: (tab: 'perCapita' | 'projection') => void;
  onMetricsCalculated?: (m: {
    observedYear: number;
    observedTotal: number;
    projected2030: number;
    target2030: number;
    onPace: boolean;
  }) => void;
  onEvPercentCalculated?: (percent: number) => void;
}

type FleetRow = {
  year: number;
  vehicle_population?: number;
  fuel_type?: string;
  class?: string;
  geofips?: number;
};

export const EVFleetAdoption: React.FC<EVFleetAdoptionProps> = ({ 
  location, 
  geofips, 
  activeTab = 'projection',
  embedOnly = false,
  onTabChange,
  onMetricsCalculated,
  onEvPercentCalculated
}) => {
  const perCapitaChartRef = useRef<HTMLCanvasElement>(null);
  const projectionChartRef = useRef<HTMLCanvasElement>(null);
  const perCapitaChartInstance = useRef<Chart | null>(null);
  const projectionChartInstance = useRef<Chart | null>(null);
  const metricsCallbackRef = useRef(onMetricsCalculated);
  const evPercentCallbackRef = useRef(onEvPercentCalculated);
  const isMountedRef = useRef(true);
  const resizeTokenRef = useRef(0);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    metricsCallbackRef.current = onMetricsCalculated;
  }, [onMetricsCalculated]);

  useEffect(() => {
    evPercentCallbackRef.current = onEvPercentCalculated;
  }, [onEvPercentCalculated]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    console.log('EVFleetAdoption useEffect triggered', { location, geofips });
    const load = async () => {
      if (!location || geofips == null) {
        console.log('EVFleetAdoption: No location, skipping load');
        setLoading(false);
        return;
      }
      console.log('EVFleetAdoption: Starting data load for', location, geofips);
      setLoading(true);
      setError(null);
      try {
        // Determine geofips for statewide
        const stateGeofips = 6;
        // Build filters
        const fuels = ['Electric', 'PluginHybrid'];
        // Selected location EV fleet - try emfac2025_fleet_mat first, fallback to emfac2021_fleet_mat
        let fleetData: any[] = [];
        
        // Try emfac2025_fleet_mat (EMFAC2025) first
        const { data: fleet2025Data, error: fleet2025Err } = await supabaseClient
          .from('emfac2025_fleet_mat')
          .select('year, vehicle_population, fuel_type, class, geofips, location_name')
          .eq('geofips', geofips || 0)
          .eq('geography_type', 'City')
          .in('fuel_type', fuels)
          .eq('class', 'LDV')
          .gte('year', 2005)
          .lte('year', 2023)
          .order('year', { ascending: true });
        
        if (!fleet2025Err && fleet2025Data) {
          fleetData = fleet2025Data;
        } else {
          // Fallback to emfac2021_fleet_mat (EMFAC2021)
          const { data: fleet2021Data, error: fleet2021Err } = await supabaseClient
            .from('emfac2021_fleet_mat')
            .select('year, vehicle_population, fuel_type, class, geofips, location_name')
            .eq('geofips', geofips || 0)
            .eq('geography_type', 'City')
            .in('fuel_type', fuels)
            .eq('class', 'LDV')
            .gte('year', 2005)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (fleet2021Err) {
            throw fleet2021Err;
          }
          fleetData = fleet2021Data || [];
        }
        
        // Statewide EV fleet - try emfac2025_fleet_mat first, fallback to emfac2021_fleet_mat
        let stateFleet: any[] = [];
        
        // Try emfac2025_fleet_mat (EMFAC2025) first
        const { data: state2025Fleet, error: state2025Err } = await supabaseClient
          .from('emfac2025_fleet_mat')
          .select('year, vehicle_population, fuel_type, class, geofips')
          .eq('geofips', 6)
          .eq('geography_type', 'State')
          .in('fuel_type', fuels)
          .eq('class', 'LDV')
          .gte('year', 2005)
          .lte('year', 2023)
          .order('year', { ascending: true });
        
        if (!state2025Err && state2025Fleet) {
          stateFleet = state2025Fleet;
        } else {
          // Fallback: aggregate all cities from emfac2021_fleet_mat (EMFAC2021)
          const { data: state2021Fleet, error: state2021Err } = await supabaseClient
            .from('emfac2021_fleet_mat')
            .select('year, vehicle_population, fuel_type, class, geofips')
            .gte('geofips', 600000)
            .lt('geofips', 700000)
            .eq('geography_type', 'City')
            .in('fuel_type', fuels)
            .eq('class', 'LDV')
            .gte('year', 2005)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (state2021Err) {
            throw state2021Err;
          }
          stateFleet = state2021Fleet || [];
        }
        // Population for per-capita
        const [{ data: popSelected }, { data: popState }] = await Promise.all([
          supabaseClient
            .from('population')
            .select('year, population, geofips')
            .eq('geofips', geofips || 0)
            .order('year', { ascending: true }),
          supabaseClient
            .from('population')
            .select('year, population, geofips')
            .eq('geofips', stateGeofips)
            .order('year', { ascending: true })
        ]);
        const sumByYearFuel = (rows: FleetRow[]) => {
          const map = new Map<number, { Electric: number; PluginHybrid: number; total: number }>();
          rows.forEach(r => {
            const key = r.year;
            const entry = map.get(key) || { Electric: 0, PluginHybrid: 0, total: 0 };
            const fuelKey = (r.fuel_type === 'PluginHybrid' ? 'PluginHybrid' : 'Electric') as 'Electric' | 'PluginHybrid';
            const amount = r.vehicle_population || 0;
            entry[fuelKey] += amount;
            entry.total = entry.Electric + entry.PluginHybrid;
            map.set(key, entry);
          });
          return map;
        };
        const byYearSelected = sumByYearFuel((fleetData || []) as FleetRow[]);
        // Aggregate state data: sum all cities for each year/fuel_type combination
        const byYearState = sumByYearFuel((stateFleet || []) as FleetRow[]);
        const years = Array.from(new Set([...(fleetData || []).map(r => r.year), ...(stateFleet || []).map(r => r.year)])).sort((a, b) => a - b);
        console.log('EVFleetAdoption: Data processed', { 
          fleetDataLength: fleetData?.length, 
          stateFleetLength: stateFleet?.length, 
          years: years.length,
          byYearSelected: byYearSelected.size,
          byYearState: byYearState.size
        });
        // Render per-capita chart (sum of EV + PHEV, per 1000 residents)
        const perCapSelected = years.map(y => {
          const total = byYearSelected.get(y)?.total || 0;
          const pop = (popSelected || []).find(p => p.year === y)?.population || 0;
          return pop > 0 ? (total / pop) * 1000 : 0;
        });
        const perCapState = years.map(y => {
          const total = byYearState.get(y)?.total || 0;
          const pop = (popState || []).find(p => p.year === y)?.population || 0;
          return pop > 0 ? (total / pop) * 1000 : 0;
        });
        if (perCapitaChartInstance.current) perCapitaChartInstance.current.destroy();
        if (perCapitaChartRef.current) {
          const ctx2 = perCapitaChartRef.current.getContext('2d');
          if (ctx2) {
            // Clear any existing chart
            ctx2.clearRect(0, 0, perCapitaChartRef.current.width, perCapitaChartRef.current.height);
            perCapitaChartInstance.current = new Chart(ctx2, {
              type: 'bar',
              data: {
                labels: years,
                datasets: [
                  { label: `${location} (per 1,000)`, data: perCapSelected, backgroundColor: '#2563eb' },
                  { label: 'California (per 1,000)', data: perCapState, backgroundColor: '#f59e0b' }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                plugins: { 
                  legend: { ...standardLegend, display: !embedOnly },
                  title: embedOnly ? { display: false } : standardTitle(`EV Adoption per 1,000 Residents - ${location}`)
                },
                scales: { 
                  x: {
                    title: {
                      display: true,
                      text: 'Year',
                      font: {
                        weight: 'bold' as const
                      }
                    },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45,
                      padding: 10
                    },
                    grid: {
                      display: false
                    }
                  },
                  y: { 
                    title: { 
                      display: true, 
                      text: 'EVs per 1,000 residents',
                      font: {
                        weight: 'bold' as const
                      }
                    },
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)'
                    }
                  } 
                }
              }
            });
          }
        }
        // Prepare CSV rows
        // Projection to 2030 using last ~5-year exponential growth trend
        const observedTotalsByYear = years.map(y => byYearSelected.get(y)?.total || 0);
        const lastObservedYear = years.length > 0 ? years[years.length - 1] : 2023;
        const targetYear = 2030;
        const windowYears = 5;
        // Find start year for CAGR window
        let startIdx = Math.max(0, years.length - (windowYears + 1));
        // Ensure non-zero start
        while (startIdx < years.length - 1 && observedTotalsByYear[startIdx] <= 0) {
          startIdx++;
        }
        const startYear = years[startIdx] || lastObservedYear - windowYears;
        const endYear = lastObservedYear;
        const startVal = observedTotalsByYear[startIdx] || 0;
        const endVal = observedTotalsByYear[years.length - 1] || 0;
        const periods = Math.max(1, endYear - startYear);
        let cagr = 0;
        if (startVal > 0 && endVal > 0) {
          cagr = Math.pow(endVal / startVal, 1 / periods) - 1;
        } else {
          cagr = 0.15; // fallback growth
        }
        const projectionYears: number[] = [];
        for (let y = lastObservedYear + 1; y <= targetYear; y++) projectionYears.push(y);
        const projectionValues: number[] = [];
        projectionYears.forEach((_y, idx) => {
          const next = endVal * Math.pow(1 + cagr, idx + 1);
          projectionValues.push(next);
        });
        // Target allocation: 5,000,000 statewide EVs by 2030
        const popCA2030 = (popState || []).find(p => p.year === 2030)?.population || 0;
        const popSel2030 = (popSelected || []).find(p => p.year === 2030)?.population || 0;
        const targetCount2030 = popCA2030 > 0 ? (5_000_000 * (popSel2030 / popCA2030)) : 0;
        const targetLineYears = [...years, ...projectionYears];
        const targetSeries = targetLineYears.map(() => targetCount2030);
        // Render projection chart
        if (projectionChartInstance.current) projectionChartInstance.current.destroy();
        if (projectionChartRef.current) {
          const pctx = projectionChartRef.current.getContext('2d');
          if (pctx) {
            const canvas = projectionChartRef.current;
            // Don't manually set canvas dimensions - let Chart.js handle it with responsive: true
            // Just clear the canvas
            pctx.clearRect(0, 0, canvas.width || 400, canvas.height || 400);
            console.log('EVFleetAdoption: Creating projection chart', {
              canvasWidth: canvas.width,
              canvasHeight: canvas.height,
              labelsCount: targetLineYears.length,
              observedDataCount: years.length,
              projectionDataCount: projectionValues.length,
              targetDataCount: targetSeries.length
            });
            projectionChartInstance.current = new Chart(pctx, {
              type: 'line',
              data: {
                labels: targetLineYears,
                datasets: [
                  {
                    label: 'Observed total EVs',
                    data: years.map(y => byYearSelected.get(y)?.total || 0),
                    borderColor: '#2563eb',
                    backgroundColor: 'rgba(37, 99, 235, 0.2)',
                    borderWidth: 2,
                    fill: false,
                    pointRadius: 2
                  },
                  {
                    label: 'Projected total EVs (to 2030)',
                    data: [...years.map(() => null), ...projectionValues],
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.2)',
                    borderDash: [6, 4],
                    borderWidth: 4,
                    fill: false,
                    pointRadius: 2
                  },
                  {
                    label: '2030 target (share of 5M statewide)',
                    data: targetSeries,
                    borderColor: '#ef4444',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    borderWidth: 4,
                    fill: false,
                    pointRadius: 0
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                resizeDelay: 0,
                plugins: { 
                  legend: { ...standardLegend, display: !embedOnly },
                  title: embedOnly ? { display: false } : standardTitle(`EV Adoption Forecast - ${location}`)
                },
                scales: { 
                  x: {
                    title: {
                      display: true,
                      text: 'Year',
                      font: {
                        weight: 'bold' as const
                      }
                    },
                    ticks: {
                      maxRotation: 45,
                      minRotation: 45,
                      padding: 10
                    },
                    grid: {
                      display: false
                    }
                  },
                  y: { 
                    title: { 
                      display: true, 
                      text: 'Vehicles (count)',
                      font: {
                        weight: 'bold' as const
                      }
                    },
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)'
                    }
                    } 
                }
              }
            });
            console.log('EVFleetAdoption: Projection chart created successfully', {
              chartId: projectionChartInstance.current?.id
            });
          } else {
            console.warn('EVFleetAdoption: Failed to get 2d context for projection chart');
          }
        } else {
          console.warn('EVFleetAdoption: projectionChartRef.current is null');
        }
        // On-pace message
        const projected2030 = projectionValues.length > 0 ? projectionValues[projectionValues.length - 1] : endVal;
        const onPace = projected2030 >= targetCount2030 && targetCount2030 > 0;
        // Expose metrics to parent for Overview text
        if (metricsCallbackRef.current) {
          metricsCallbackRef.current({
            observedYear: lastObservedYear,
            observedTotal: endVal,
            projected2030: projected2030,
            target2030: targetCount2030,
            onPace
          });
        }
        
        // Calculate EV percentage for latest year (2023)
        if (evPercentCallbackRef.current && geofips) {
          try {
            // Get total LDV vehicles (all fuel types) for 2023 - try emfac2025_fleet_mat first, fallback to emfac2021_fleet_mat
            let allLdvData: any[] = [];
            
            const { data: ldv2025Data, error: ldv2025Err } = await supabaseClient
              .from('emfac2025_fleet_mat')
              .select('vehicle_population')
              .eq('geofips', geofips)
              .eq('geography_type', 'City')
              .eq('year', 2023)
              .eq('class', 'LDV');
            
            if (!ldv2025Err && ldv2025Data) {
              allLdvData = ldv2025Data;
            } else {
              const { data: ldv2021Data, error: ldv2021Err } = await supabaseClient
                .from('emfac2021_fleet_mat')
                .select('vehicle_population')
                .eq('geofips', geofips)
                .eq('geography_type', 'City')
                .eq('year', 2023)
                .eq('class', 'LDV');
              
              if (!ldv2021Err && ldv2021Data) {
                allLdvData = ldv2021Data;
              }
            }
            
            const totalLDV = allLdvData ? allLdvData.reduce((sum: number, r: any) => sum + (Number(r.vehicle_population) || 0), 0) : 0;
            
            if (totalLDV > 0 && endVal > 0) {
              const evPercent = (endVal / totalLDV) * 100;
              evPercentCallbackRef.current(evPercent);
            }
          } catch (err) {
            console.warn('Failed to calculate EV percentage:', err);
          }
        }
        
        if (isMountedRef.current) {
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load EV fleet adoption data:', err);
        if (isMountedRef.current) {
          setError('Failed to load EV adoption data');
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      if (perCapitaChartInstance.current) perCapitaChartInstance.current.destroy();
      if (projectionChartInstance.current) projectionChartInstance.current.destroy();
    };
  }, [location, geofips, embedOnly]);

  // Update chart visibility when activeTab changes
  useEffect(() => {
    if (!location || loading) return;
    
    console.log('EVFleetAdoption: Tab changed to', activeTab);
    
    // Force chart resize/update when tab changes to ensure proper rendering
    resizeTokenRef.current += 1;
    const token = resizeTokenRef.current;
    const getChartAndCanvas = () => ({
      chart: activeTab === 'perCapita' ? perCapitaChartInstance.current : projectionChartInstance.current,
      canvas: activeTab === 'perCapita' ? perCapitaChartRef.current : projectionChartRef.current
    });

    const { chart: initialChart, canvas: initialCanvas } = getChartAndCanvas();
    if (initialChart && initialCanvas) {
      // Wait for canvas to be visible before resizing
      const checkAndResize = () => {
        if (resizeTokenRef.current !== token) return;
        const { chart: currentChart, canvas: currentCanvas } = getChartAndCanvas();
        if (!currentChart || !currentCanvas) return;
        
        try {
          // Check if canvas is actually in the DOM
          if (!currentCanvas.isConnected || !currentCanvas.ownerDocument) {
            console.warn(`EVFleetAdoption: Canvas not in DOM for tab: ${activeTab}`);
            return;
          }
          
          // Check if canvas is actually visible
          const style = window.getComputedStyle(currentCanvas);
          if (style.display === 'none') {
            // Canvas is still hidden - wait a bit more
            resizeTimeoutRef.current = setTimeout(checkAndResize, 100);
            return;
          }
          
          // Ensure canvas has proper dimensions
          const container = currentCanvas.parentElement;
          if (container) {
            const rect = container.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              // Canvas is visible and has dimensions - now we can resize
              requestAnimationFrame(() => {
                if (currentChart && currentCanvas && currentCanvas.isConnected) {
                  try {
                    // Force a resize by updating the chart
                    currentChart.update('none');
                    // Small delay then resize to ensure layout is stable
                    resizeTimeoutRef.current = setTimeout(() => {
                      if (currentChart && currentCanvas && currentCanvas.isConnected) {
                        try {
                          currentChart.resize();
                          currentChart.update('none');
                          console.log(`EVFleetAdoption: Chart resized for tab: ${activeTab}`);
                        } catch (resizeError) {
                          console.warn('Chart resize failed:', resizeError);
                        }
                      }
                    }, 50);
                  } catch (updateError) {
                    console.warn('Chart update failed:', updateError);
                  }
                }
              });
            } else {
              // Container has no dimensions yet - wait a bit more
              resizeTimeoutRef.current = setTimeout(checkAndResize, 100);
            }
          } else {
            console.warn(`EVFleetAdoption: Canvas has no parent container for tab: ${activeTab}`);
          }
        } catch (error) {
          console.warn('Chart resize check failed:', error);
        }
      };
      
      // Start checking after a short delay
      resizeTimeoutRef.current = setTimeout(checkAndResize, 100);
    } else {
      // If chart doesn't exist yet, log a warning
      console.warn(`EVFleetAdoption: Chart instance or canvas not found for tab: ${activeTab}`, {
        chart: !!initialChart,
        canvas: !!initialCanvas
      });
    }
  }, [activeTab, location, loading]);


  console.log('EVFleetAdoption render', { location, geofips, loading, error, activeTab });
  
  const hasActiveChart = activeTab === 'perCapita'
    ? !!perCapitaChartInstance.current
    : !!projectionChartInstance.current;
  const showLoading = loading && !hasActiveChart;

  const chartContent = (
    <div style={{ height: embedOnly ? '100%' : 400, width: '100%', position: 'relative' }}>
      <canvas ref={perCapitaChartRef} style={{ 
        height: '100%', 
        width: '100%', 
        position: 'absolute',
        top: 0,
        left: 0,
        display: activeTab === 'perCapita' ? 'block' : 'none'
      }} />
      <canvas ref={projectionChartRef} style={{ 
        height: '100%', 
        width: '100%', 
        position: 'absolute',
        top: 0,
        left: 0,
        display: activeTab === 'projection' ? 'block' : 'none'
      }} />
      {showLoading && (
        <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading EV data...</div>
      )}
      {error && (
        <div style={{ color: '#b91c1c', marginTop: 8 }}>{error}</div>
      )}
    </div>
  );

  return chartContent;
};

export default EVFleetAdoption;


